const _excluded = ["indexName", "resultAttributes", "autocompleteAttributes", "autocompleteResults", "autocompleteFuzziness", "autosuggestAttributes", "autosuggestResults", "reverseTypeOrder", "searchSettings", "renderProps", "queryFilters", "children"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
import React, { useMemo } from 'react';
import SearchkitWrapper from '../Wrapper';
import mapAutocompleteOption from './helpers/mapAutocompleteOption';

// Subcomponents.
import SearchAutosuggest from '../Autosuggest';
const DEFAULT_AUTOCOMPLETE_FUZZINESS = 2;
/**
 * This component renders a searchbar with optional autocomplete and autosuggest functionality.
 *
 * @property {string} indexName -
 * Name of the search index.
 * @property {string[]} resultAttributes -
 * Attributes you want to use for rendering the results.
 * If empty, it will default to the autosuggest attributes.
 * @property {string[]} autocompleteAttributes -
 * Attributes for searching autocomplete results.
 * If empty, the search query won't return any autocomplete results.
 * @property {number} autocompleteResults -
 * Max number of autocomplete results to render.
 * If set to 0, the search query won't return any autocomplete results.
 * @property {number} autocompleteFuzziness -
 * The level of fuzziness for autocomplete results.
 * @property {string[]} autosuggestAttributes -
 * Attributes for searching autosuggest results.
 * If empty, the search query won't return any autosuggest results.
 * @property {number} autosuggestResults -
 * Max number of autosuggest results to render.
 * If set to 0, the search query won't return any autosuggest results.
 * @property {boolean} reverseTypeOrder -
 * By default, results are ordered by autosuggest items first.
 * Set this to true if you want to reverse this and show autocomplete items first.
 * @property {Object} searchSettings -
 * Additional Searchkit search settings.
 * @property {Object} renderProps -
 * Additional render props for the default SearchAutosuggest rendering.
 * @property {Object} queryFilters -
 * To optionally extend below default query with an
 * ElasticSearch base filter. For example, to filter on domain.
 * @property {React.ReactNode} children -
 * Any child nodes you want to render.
 * Will overwrite the default rendering if used.
 *
 * @returns {React.ReactElement} SearchAutocomplete
 */
// eslint-disable-next-line max-len
const SearchAutocomplete = props => {
  const {
      indexName,
      resultAttributes,
      autocompleteAttributes = [],
      autocompleteResults = 0,
      autocompleteFuzziness = DEFAULT_AUTOCOMPLETE_FUZZINESS,
      autosuggestAttributes = [],
      autosuggestResults = 0,
      reverseTypeOrder = false,
      searchSettings,
      renderProps,
      queryFilters = [],
      children
    } = props,
    restProps = _objectWithoutProperties(props, _excluded);
  const requestOptions = useMemo(() => ({
    hooks: {
      beforeSearch: requests => {
        requests.forEach(_ref => {
          var _request$params;
          let {
            body,
            request
          } = _ref;
          if (!((_request$params = request.params) !== null && _request$params !== void 0 && _request$params.query)) {
            return;
          }

          // Alter body.suggest when we want to use autocompletion.
          if (autocompleteResults > 0 && autocompleteAttributes.length > 0) {
            const filters = Object.fromEntries(autocompleteAttributes.map(attribute => {
              var _request$params2;
              return [attribute, {
                prefix: (_request$params2 = request.params) === null || _request$params2 === void 0 ? void 0 : _request$params2.query,
                completion: {
                  size: autocompleteResults,
                  field: attribute,
                  skip_duplicates: true,
                  fuzzy: {
                    fuzziness: autocompleteFuzziness
                  }
                }
              }];
            }));

            // eslint-disable-next-line no-param-reassign
            body.suggest = _objectSpread({
              text: request.params.query
            }, filters);
          }

          // Alter body.query when we want to use autosuggest,
          // otherwise set size to 0, so we don't fetch any hits.
          if (autosuggestResults > 0 && autosuggestAttributes.length > 0) {
            // eslint-disable-next-line no-param-reassign
            body.query = {
              bool: {
                filter: queryFilters,
                must: {
                  bool: {
                    should: [{
                      multi_match: {
                        query: request.params.query,
                        fields: autosuggestAttributes
                      }
                    }]
                  }
                }
              }
            };
          } else {
            // eslint-disable-next-line no-param-reassign
            body.size = 0;
          }
        });
        return Promise.resolve(requests);
      },
      afterSearch: (_, responses) => {
        responses.forEach(response => {
          // Add the autocomplete options to the list of hits when we use autocompletion.
          if (response.suggest) {
            const autocompleteOptions = [];

            // Fill the autocomplete options with query suggestions for each autocomplete attribute.
            autocompleteAttributes.forEach(attribute => {
              var _response$suggest;
              const suggest = (_response$suggest = response.suggest) === null || _response$suggest === void 0 || (_response$suggest = _response$suggest[attribute]) === null || _response$suggest === void 0 ? void 0 : _response$suggest[0];
              const suggestOptions = suggest === null || suggest === void 0 ? void 0 : suggest.options;
              if (!suggestOptions) {
                return;
              }

              // Options can be an array of options or a single option.
              const options = Array.isArray(suggestOptions) ? suggestOptions : [suggestOptions];
              const mappedOptions = options.map(option => mapAutocompleteOption(option, attribute, suggest.text));
              mappedOptions.forEach(option => {
                autocompleteOptions.push(option);
              });
            });
            if (autocompleteOptions.length === 0) {
              return;
            }

            // Sort the autocomplete options by score (asc), and slice off the amount
            // of options we require through the autocompleteResults prop.
            const sortedAutocompleteOptions = autocompleteOptions.sort((_ref2, _ref3) => {
              let {
                _score: a = 0
              } = _ref2;
              let {
                _score: b = 0
              } = _ref3;
              return b - a;
            }).slice(0, autocompleteResults);

            // Push the sorted options to the list of hits, so we
            // can render them and set the autocomplete_text property.
            sortedAutocompleteOptions.forEach(option => {
              // By default, results are ordered by autosuggest items first.
              // We can reverse this, putting autocomplete results first, if
              // reverseTypeOrder is true.
              // eslint-disable-next-line max-len
              const insertIndex = Math.max(response.hits.hits.length - (reverseTypeOrder ? autosuggestResults : 0), 0);
              response.hits.hits.splice(insertIndex, 0, _objectSpread(_objectSpread({}, option), {}, {
                // eslint-disable-next-line no-underscore-dangle
                _id: option._id || '',
                // eslint-disable-next-line no-underscore-dangle
                _index: option._index || indexName,
                _source: _objectSpread(_objectSpread({}, option._source), {}, {
                  autocomplete_text: [option.text]
                })
              }));
            });
          }
        });
        return Promise.resolve(responses);
      }
    }
  }), [autocompleteAttributes, autocompleteFuzziness, autocompleteResults, autosuggestAttributes, autosuggestResults, indexName, reverseTypeOrder, queryFilters]);
  return /*#__PURE__*/React.createElement(SearchkitWrapper, _extends({}, restProps, {
    indexName: indexName,
    routing: false,
    onlyFetchWithQuery: true,
    hitsPerPage: autosuggestResults,
    searchSettings: _objectSpread(_objectSpread({}, searchSettings), {}, {
      search_attributes: autosuggestAttributes,
      result_attributes: resultAttributes || autosuggestAttributes
    }),
    requestOptions: requestOptions
  }), children || /*#__PURE__*/React.createElement(SearchAutosuggest
  // Add default props when renderProps are not provided.
  , _extends({
    getSuggestionValue: (renderProps === null || renderProps === void 0 ? void 0 : renderProps.getSuggestionValue) || (hit => {
      var _hit$autocomplete_tex;
      return ((_hit$autocomplete_tex = hit.autocomplete_text) === null || _hit$autocomplete_tex === void 0 ? void 0 : _hit$autocomplete_tex[0]) || '';
    }),
    renderSuggestion: (renderProps === null || renderProps === void 0 ? void 0 : renderProps.renderSuggestion) || (hit => {
      var _hit$autocomplete_tex2;
      return ((_hit$autocomplete_tex2 = hit.autocomplete_text) === null || _hit$autocomplete_tex2 === void 0 ? void 0 : _hit$autocomplete_tex2[0]) || '';
    })
  }, renderProps)));
};
SearchAutocomplete.DefaultChild = SearchAutosuggest;
export default SearchAutocomplete;